import axiosInstance, { API_ROUTES } from '../lib/http/httpRequest';
import paymentAxiosInstance from '@/lib/http/paymentServiseHttpRequests';
import { AxiosResponse } from 'axios';
import {
    APP_FOR_PAYMENT,
    HT_PAYMENT_PROVIDER,
    HypertrackerPriceResponse,
    PAYMENT_METHOD,
    PLANS,
    Price,
    Subscription,
    SUBSCRIPTION_DURATION,
    UpgradeState,
    UserOrder,
} from '@/state/payments/paymentsTypes';
import { Country } from '@/state/general/generalTypes';

interface CPSubscriptionPayload {
    accountType: PLANS;
    duration: SUBSCRIPTION_DURATION;
    params: {
        coupon?: string;
        countryCode?: string;
    };
}

interface CPSubscriptionResponse {
    txn_id: string;
    amount: string | number;
    checkout_url: string;
    qrcode_url: string;
    status_url: string;
    address: string;
    confirms_needed: string | number;
    timeout: number;
}

interface PaymentServiceSubscriptionPayload {
    applicationCode: APP_FOR_PAYMENT;
    selectedPlan: string;
    selectedPeriod: string;
    successURL: string;
    cancelURL: string;
}

interface PaymentServiceUpgradePayload {
    applicationCode: APP_FOR_PAYMENT;
    productCode: string;
    subscriptionPlanDuration: string;
}

interface PaymentServiceCoinpaymentsUpgradePayload extends PaymentServiceUpgradePayload {
    successURL: string;
    cancelURL: string;
}

interface PaymentServiceLlamapayUpgradePayload {
    subscriptionId: string;
    productCode: string;
    subscriptionPlanDuration?: string;
    successURL: string;
    cancelURL: string;
}

export const getAllSubscriptionDetails = (): Promise<AxiosResponse<Subscription[]>> =>
    axiosInstance.get(`${API_ROUTES.PAYMENTS}/subscriptions`);

export const getAllPaypalDetails = (): Promise<AxiosResponse<UserOrder[]>> =>
    axiosInstance.get(`${API_ROUTES.PAYMENTS}/payments`);

export const startSubscription = (payload: {
    accountType: PLANS;
    duration: SUBSCRIPTION_DURATION;
    paymentMethod: PAYMENT_METHOD;
    params: any;
}): Promise<AxiosResponse<any>> =>
    axiosInstance.get(
        `${API_ROUTES.PAYMENTS}/${payload.paymentMethod}/subscribe/${payload.accountType}/${payload.duration}`,
        { params: payload.params },
    );

export const finalizeSubscription = (payload: {
    paypalId: string;
}): Promise<AxiosResponse<Partial<any>>> =>
    axiosInstance.get(`${API_ROUTES.PAYMENTS}/paypal/finalize-subscription/${payload.paypalId}`);

export const cancelExistingSubscription = (payload: {
    cancelReason: string;
}): Promise<AxiosResponse<any>> =>
    axiosInstance.post(`${API_ROUTES.PAYMENTS}/paypal/cancel-subscription/`, {
        cancelReason: payload.cancelReason,
    });

export const getNonRedeemedCouponsApi = (): Promise<AxiosResponse<any>> =>
    axiosInstance.get(`${API_ROUTES.COUPONS}/non-redeemed`);

export const checkCoupon = ({ couponCode }) =>
    axiosInstance.get(`${API_ROUTES.COUPONS}/checkCoupon/${couponCode}`);

export const startCPSubscription = ({
    accountType,
    duration,
    params,
}: CPSubscriptionPayload): Promise<AxiosResponse<CPSubscriptionResponse>> =>
    axiosInstance.get(`${API_ROUTES.PAYMENTS}/cp/order/${accountType}/${duration}`, { params });

export const getCountriesApi = (): Promise<AxiosResponse<Country[]>> =>
    axiosInstance.get(`/countries`);

export const getPrices = (): Promise<AxiosResponse<Price[]>> =>
    axiosInstance.get(`${API_ROUTES.PAYMENTS}/prices`);

export const getStripePortal = (): Promise<AxiosResponse<{ url: string }>> =>
    axiosInstance.get(`${API_ROUTES.PAYMENTS}/stripe/customer-portal`);

export const getStripeSubscriptionInvoice = (payload: {
    accountType: PLANS;
    duration: SUBSCRIPTION_DURATION;
}): Promise<AxiosResponse<UpgradeState>> =>
    axiosInstance.get(
        `${API_ROUTES.PAYMENTS}/stripe/subscription-invoice/${payload.accountType}/${payload.duration}`,
    );

export const getCPSubscriptionInvoice = (payload: {
    accountType: PLANS;
    duration: SUBSCRIPTION_DURATION;
}): Promise<AxiosResponse<UpgradeState>> =>
    axiosInstance.get(
        `${API_ROUTES.PAYMENTS}/cp/subscription-invoice/${payload.accountType}/${payload.duration}`,
    );

export const upgradeStripeSubscription = (payload: {
    accountType: PLANS;
    subscriptionDuration: SUBSCRIPTION_DURATION;
}) =>
    axiosInstance.get(
        `${API_ROUTES.PAYMENTS}/stripe/upgrade-subscription/${payload.accountType}/${payload.subscriptionDuration}`,
    );
export const upgradeCPSubscription = (payload: {
    accountType: PLANS;
    subscriptionDuration: SUBSCRIPTION_DURATION;
}) =>
    axiosInstance.get(
        `${API_ROUTES.PAYMENTS}/cp/upgrade-subscription/${payload.accountType}/${payload.subscriptionDuration}`,
    );

export const getActualSubscription = (): Promise<AxiosResponse<Subscription | null>> =>
    axiosInstance.get(`${API_ROUTES.PAYMENTS}/subscription/actual`);

// Hypertracker payment

export const getHyperTrackerPrices = (
    appCode: APP_FOR_PAYMENT = APP_FOR_PAYMENT.HYPERTRACKER,
): Promise<AxiosResponse<HypertrackerPriceResponse>> =>
    paymentAxiosInstance.get(`/products/${appCode}/prices`);

export const getActiveHyperTrackerSubscription = (): Promise<AxiosResponse<any>> =>
    paymentAxiosInstance.get(`${API_ROUTES.PAYMENTS}/subscriptions/active`);

export const suspendActiveSubscription = (payload: { subscriptionUuid: string }) =>
    paymentAxiosInstance.put(`/payments/stripe/suspend/${payload.subscriptionUuid}/charges`);

export const unpauseActiveSubscription = (payload: { subscriptionUuid: string }) =>
    paymentAxiosInstance.put(`/payments/stripe/unpause/${payload.subscriptionUuid}/charges`);

export const createStripeSubscription = ({
    applicationCode,
    selectedPlan,
    selectedPeriod,
    successURL,
    cancelURL,
}: PaymentServiceSubscriptionPayload): Promise<AxiosResponse<any>> =>
    paymentAxiosInstance.get(
        `${API_ROUTES.PAYMENTS}/stripe/subscribe/${applicationCode}/${selectedPlan}/${selectedPeriod}?successUrl=${successURL}&cancelUrl=${cancelURL}`,
    );

export const createCoinpaymentsSubscription = ({
    applicationCode,
    selectedPlan,
    selectedPeriod,
    successURL,
    cancelURL,
}: PaymentServiceSubscriptionPayload): Promise<AxiosResponse<any>> =>
    paymentAxiosInstance.get(
        `${API_ROUTES.PAYMENTS}/coinpayments/subscribe/${applicationCode}/${selectedPlan}/${selectedPeriod}?successUrl=${successURL}&cancelUrl=${cancelURL}`,
    );

export const createLlamapaySubscription = ({
    applicationCode,
    selectedPlan,
    selectedPeriod,
    successURL,
    cancelURL,
}: PaymentServiceSubscriptionPayload): Promise<AxiosResponse<any>> =>
    paymentAxiosInstance.get(
        `${API_ROUTES.PAYMENTS}/llamapay/subscribe/${applicationCode}/${selectedPlan}/${selectedPeriod}?successUrl=${successURL}&cancelUrl=${cancelURL}`,
    );

export const getUpgradeSubscriptionInvoicesPath = (payload: {
    applicationCode: APP_FOR_PAYMENT;
    paymentProvider: HT_PAYMENT_PROVIDER;
}) =>
    `${API_ROUTES.PAYMENTS}/${payload.paymentProvider}/upgrade-subscription-invoice/${payload.applicationCode}`;

export const getStripeUpgradeSubscriptionInvoice = (payload: {
    applicationCode: APP_FOR_PAYMENT;
    productCode: string;
    subscriptionPlanDuration: string;
}): Promise<AxiosResponse<UpgradeState>> =>
    paymentAxiosInstance.get(
        `${API_ROUTES.PAYMENTS}/stripe/upgrade-subscription-invoice/${payload.applicationCode}/${payload.productCode}/${payload.subscriptionPlanDuration}`,
    );

export const getStripeUpgradeSubscriptionInvoices = (payload: {
    applicationCode: APP_FOR_PAYMENT;
}): Promise<AxiosResponse<UpgradeState[]>> =>
    paymentAxiosInstance.get(
        getUpgradeSubscriptionInvoicesPath({
            applicationCode: payload.applicationCode,
            paymentProvider: HT_PAYMENT_PROVIDER.STRIPE,
        }),
    );

export const getCoinpaymentsUpgradeSubscriptionInvoice = (payload: {
    applicationCode: APP_FOR_PAYMENT;
    productCode: string;
    subscriptionPlanDuration: string;
}): Promise<AxiosResponse<UpgradeState>> =>
    paymentAxiosInstance.get(
        `${API_ROUTES.PAYMENTS}/coinpayments/upgrade-subscription-invoice/${payload.applicationCode}/${payload.productCode}/${payload.subscriptionPlanDuration}`,
    );

export const getCoinpaymentsUpgradeSubscriptionInvoices = (payload: {
    applicationCode: APP_FOR_PAYMENT;
}): Promise<AxiosResponse<UpgradeState[]>> =>
    paymentAxiosInstance.get(
        getUpgradeSubscriptionInvoicesPath({
            applicationCode: payload.applicationCode,
            paymentProvider: HT_PAYMENT_PROVIDER.COINPAYMENTS,
        }),
    );

export const getLlamapayUpgradeSubscriptionInvoice = (payload: {
    applicationCode: APP_FOR_PAYMENT;
    productCode: string;
    subscriptionPlanDuration: string;
}): Promise<AxiosResponse<UpgradeState>> =>
    paymentAxiosInstance.get(
        `${API_ROUTES.PAYMENTS}/llamapay/upgrade-subscription-invoice/${payload.applicationCode}/${payload.productCode}/${payload.subscriptionPlanDuration}`,
    );

export const getLlamapayUpgradeSubscriptionInvoices = (payload: {
    applicationCode: APP_FOR_PAYMENT;
}): Promise<AxiosResponse<UpgradeState[]>> =>
    paymentAxiosInstance.get(
        getUpgradeSubscriptionInvoicesPath({
            applicationCode: payload.applicationCode,
            paymentProvider: HT_PAYMENT_PROVIDER.LLAMAPAY,
        }),
    );

export const getUpgradeSubscriptionInvoice = getStripeUpgradeSubscriptionInvoice;

export const upgradeStripeApiSubscription = (payload: {
    applicationCode: APP_FOR_PAYMENT;
    productCode: string;
    subscriptionPlanDuration: string;
}): Promise<AxiosResponse<any>> =>
    paymentAxiosInstance.get(
        `${API_ROUTES.PAYMENTS}/stripe/upgrade-subscription/${payload.applicationCode}/${payload.productCode}/${payload.subscriptionPlanDuration}`,
    );

export const upgradeLlamapayApiSubscription = ({
    subscriptionId,
    productCode,
    subscriptionPlanDuration,
    successURL,
    cancelURL,
}: PaymentServiceLlamapayUpgradePayload): Promise<AxiosResponse<any>> => {
    const durationParam = subscriptionPlanDuration
        ? `&subscriptionPlanDuration=${subscriptionPlanDuration}`
        : '';

    return paymentAxiosInstance.get(
        `${API_ROUTES.PAYMENTS}/llamapay/upgrade-subscription/${subscriptionId}/${productCode}?successUrl=${successURL}&cancelUrl=${cancelURL}${durationParam}`,
    );
};

export const upgradeApiSubscription = upgradeStripeApiSubscription;

export const getPaymentServiceHistory = (): Promise<AxiosResponse<any>> =>
    paymentAxiosInstance.get(`${API_ROUTES.PAYMENTS}/history`);
